export type Role = "SUPER_ADMIN" | "OPERATIONS_ADMIN" | "BILLING_ADMIN" | "SUPPORT_ADMIN";
export type SubscriberType = "Household" | "Organization";
export type PaymentStatus = "Trial" | "Admin active" | "Paid" | "Past due" | "Grace period" | "Canceled";
export type SubscriberStatus = "Active" | "Trial" | "Restricted" | "Canceled" | "Suspended";
export type CodeStatus = "Active" | "Paused" | "Expired";
export type Tone = "green" | "amber" | "red" | "gray" | "blue" | "teal";
export type OperationsApp = "Security" | "Camera" | "Console" | "Admin";
export type OperationsSeverity = "Critical" | "High" | "Medium" | "Low";
export type OperationsStatus = "Open" | "Investigating" | "Remediating" | "Resolved" | "Failed remediation";
export type Permission =
  | "subscription.extendTrial" | "subscription.changePlan" | "subscription.applyCode" | "subscription.cancel"
  | "subscription.reactivate" | "billing.openProvider" | "code.manage" | "camera.markReview" | "member.resendInvite"
  | "admin.invite" | "admin.manageRole" | "admin.disable" | "admin.resetMfa" | "settings.changePassword";

export interface Subscriber {
  id: string;
  name: string;
  ownerEmail: string;
  type: SubscriberType;
  plan: string;
  interval: "Monthly" | "Annual";
  monthlyAmount: number;
  trialStartAt: string | null;
  trialEndsAt: string | null;
  renewalAt: string | null;
  paymentStatus: PaymentStatus;
  cameraCount: number;
  cameraAllowance: number;
  lastActivityAt: string;
  status: SubscriberStatus;
  createdAt: string;
  region: string;
  providerRef: string;
  codeApplied: string | null;
  flags: { label: string; tone: Tone }[];
  retentionDays: number;
  seats: number;
}

export interface Member {
  subscriberId: string;
  userId: string;
  name: string;
  email: string;
  role: string;
  status: "Active" | "Invited";
  lastActiveAt: string | null;
  invitedAt: string | null;
}

export interface Camera {
  subscriberId: string;
  cameraId: string;
  name: string;
  status: "Online" | "Offline";
  health: "Healthy" | "Degraded" | "Offline";
  power: string;
  thermal: string;
  storagePct: number;
  uploadQueue: number;
  lastHeartbeatAt: string;
  nightAssist: "On" | "Off" | "Unavailable";
  reviewFlagged: boolean;
}

export interface EventMeta {
  subscriberId: string;
  eventId: string;
  cameraId: string;
  cameraName: string;
  detectedAt: string;
  category: string;
  recording: "Recorded" | "Not recorded";
  durationSec: number | null;
  uploadStatus: "Uploaded" | "Queued" | "Held - billing" | "Failed";
}

export interface OperationsIssue {
  id: string;
  app: OperationsApp;
  component: string;
  severity: OperationsSeverity;
  status: OperationsStatus;
  title: string;
  detail: string;
  firstDetectedAt: string;
  lastDetectedAt: string;
  affected: string;
  fingerprint: string;
  occurrences: number;
  remediation: string;
  remediationAttempts: number;
  remediationOutcome: string;
  camera?: Camera;
}

export interface DiscountCode {
  code: string;
  description: string;
  discountType: "percent" | "fixed";
  discountValue: number;
  eligibility: "Consumer" | "Commercial" | "Both" | "Specific subscriber";
  expiresAt: string | null;
  maxRedemptions: number | null;
  redeemedCount: number;
  status: CodeStatus;
  createdBy: string;
  subscriberId?: string;
}

export interface AuditRow {
  id: string;
  occurredAt: string;
  admin: string;
  role: Role;
  action: string;
  targetType: "subscriber" | "subscription" | "code" | "camera" | "member" | "admin" | "settings";
  target: string;
  targetId: string;
  subscriberId?: string | undefined;
  subscriberType?: SubscriberType | undefined;
  reason: string;
  result: "Success" | "Failed" | "Denied" | "Pending";
  correlationId: string;
  before: Record<string, string | number | null>;
  after: Record<string, string | number | null>;
}

export interface AdminTeamMember {
  id: string;
  name: string;
  email: string;
  role: Role;
  status: "Active" | "Invited" | "Disabled";
  mfaStatus: "Enabled" | "Required" | "Reset pending";
  lastSignInAt: string | null;
  invitedBy: string;
  createdAt: string;
}

export const roles: Role[] = ["SUPER_ADMIN", "OPERATIONS_ADMIN", "BILLING_ADMIN", "SUPPORT_ADMIN"];
const perms: Record<Permission, Role[]> = {
  "subscription.extendTrial": ["SUPER_ADMIN", "BILLING_ADMIN", "SUPPORT_ADMIN"],
  "subscription.changePlan": ["SUPER_ADMIN", "BILLING_ADMIN"],
  "subscription.applyCode": ["SUPER_ADMIN", "BILLING_ADMIN"],
  "subscription.cancel": ["SUPER_ADMIN", "BILLING_ADMIN"],
  "subscription.reactivate": ["SUPER_ADMIN", "BILLING_ADMIN"],
  "billing.openProvider": ["SUPER_ADMIN", "BILLING_ADMIN"],
  "code.manage": ["SUPER_ADMIN", "BILLING_ADMIN"],
  "camera.markReview": ["SUPER_ADMIN", "OPERATIONS_ADMIN", "SUPPORT_ADMIN"],
  "member.resendInvite": ["SUPER_ADMIN", "OPERATIONS_ADMIN", "SUPPORT_ADMIN"],
  "admin.invite": ["SUPER_ADMIN", "OPERATIONS_ADMIN"],
  "admin.manageRole": ["SUPER_ADMIN"],
  "admin.disable": ["SUPER_ADMIN"],
  "admin.resetMfa": ["SUPER_ADMIN", "OPERATIONS_ADMIN"],
  "settings.changePassword": ["SUPER_ADMIN", "OPERATIONS_ADMIN", "BILLING_ADMIN", "SUPPORT_ADMIN"]
};
export const deniedText: Record<Permission, string> = {
  "subscription.extendTrial": "Billing or Support Admin required",
  "subscription.changePlan": "Billing Admin required",
  "subscription.applyCode": "Billing Admin required",
  "subscription.cancel": "Billing Admin required",
  "subscription.reactivate": "Billing Admin required",
  "billing.openProvider": "Billing Admin required",
  "code.manage": "Billing Admin required",
  "camera.markReview": "Ops or Support Admin required",
  "member.resendInvite": "Ops or Support Admin required",
  "admin.invite": "Super Admin or Operations Admin required",
  "admin.manageRole": "Super Admin required",
  "admin.disable": "Super Admin required",
  "admin.resetMfa": "Super Admin or Operations Admin required",
  "settings.changePassword": "Available to signed-in admins"
};
export function can(role: Role, permission: Permission): boolean {
  return role === "SUPER_ADMIN" || perms[permission].includes(role);
}

export const paymentTone: Record<PaymentStatus, Tone> = { Trial: "amber", "Admin active": "teal", Paid: "green", "Past due": "red", "Grace period": "amber", Canceled: "red" };
export const statusTone: Record<SubscriberStatus, Tone> = { Active: "green", Trial: "amber", Restricted: "amber", Canceled: "red", Suspended: "red" };
export const codeTone: Record<CodeStatus, Tone> = { Active: "green", Paused: "amber", Expired: "gray" };

export const subscribersSeed: Subscriber[] = [
  { id: "HH-1001", name: "Reevir Household", ownerEmail: "ejaugustin@gmail.com", type: "Household", plan: "Household Plus", interval: "Annual", monthlyAmount: 6.67, trialStartAt: "2026-09-01", trialEndsAt: "2026-10-01", renewalAt: "2027-09-01", paymentStatus: "Trial", cameraCount: 2, cameraAllowance: 8, lastActivityAt: "2026-09-23T00:41:00-04:00", status: "Trial", createdAt: "2026-09-01", region: "Miami, FL", providerRef: "sub_preview_1001", codeApplied: null, flags: [{ label: "Internal test account", tone: "blue" }], retentionDays: 30, seats: 5 },
  { id: "ORG-2040", name: "Banxito Operations", ownerEmail: "ops@banxito.com", type: "Organization", plan: "Business Growth", interval: "Monthly", monthlyAmount: 39, trialStartAt: null, trialEndsAt: null, renewalAt: "2026-10-18", paymentStatus: "Paid", cameraCount: 7, cameraAllowance: 8, lastActivityAt: "2026-09-22T19:14:00-04:00", status: "Active", createdAt: "2026-08-18", region: "New York, NY", providerRef: "sub_banxito_2040", codeApplied: "PILOT25", flags: [{ label: "Key account", tone: "teal" }], retentionDays: 30, seats: 12 },
  { id: "ORG-2175", name: "Hendrick West Lot", ownerEmail: "security@hendrick.example", type: "Organization", plan: "Business Scale", interval: "Monthly", monthlyAmount: 59, trialStartAt: null, trialEndsAt: null, renewalAt: "2026-09-25", paymentStatus: "Past due", cameraCount: 13, cameraAllowance: 16, lastActivityAt: "2026-09-21T09:22:00-04:00", status: "Restricted", createdAt: "2026-07-11", region: "Charlotte, NC", providerRef: "sub_hendrick_2175", codeApplied: null, flags: [{ label: "Payment retried 2x", tone: "amber" }], retentionDays: 60, seats: 24 },
  { id: "HH-1188", name: "Garcia Home", ownerEmail: "maria.garcia@example.com", type: "Household", plan: "Personal", interval: "Monthly", monthlyAmount: 1.99, trialStartAt: null, trialEndsAt: null, renewalAt: "2026-10-03", paymentStatus: "Paid", cameraCount: 1, cameraAllowance: 1, lastActivityAt: "2026-09-22T22:03:00-04:00", status: "Active", createdAt: "2026-09-03", region: "Orlando, FL", providerRef: "sub_garcia_1188", codeApplied: null, flags: [], retentionDays: 7, seats: 1 },
  { id: "HH-1204", name: "Patel Household", ownerEmail: "a.patel@example.com", type: "Household", plan: "Household", interval: "Annual", monthlyAmount: 4.17, trialStartAt: null, trialEndsAt: null, renewalAt: null, paymentStatus: "Canceled", cameraCount: 3, cameraAllowance: 4, lastActivityAt: "2026-08-29T11:08:00-04:00", status: "Canceled", createdAt: "2026-08-01", region: "Atlanta, GA", providerRef: "sub_patel_1204", codeApplied: "FRIENDS20", flags: [], retentionDays: 30, seats: 4 },
  { id: "ORG-2308", name: "Claricito Clinic", ownerEmail: "admin@claricito.example", type: "Organization", plan: "Business Starter", interval: "Annual", monthlyAmount: 15.2, trialStartAt: "2026-09-12", trialEndsAt: "2026-10-12", renewalAt: "2027-09-12", paymentStatus: "Grace period", cameraCount: 4, cameraAllowance: 4, lastActivityAt: "2026-09-22T13:33:00-04:00", status: "Active", createdAt: "2026-09-12", region: "Boston, MA", providerRef: "sub_clinic_2308", codeApplied: "TRIAL30", flags: [{ label: "Commercial pilot", tone: "blue" }], retentionDays: 30, seats: 8 }
];

export const membersSeed: Member[] = subscribersSeed.flatMap((subscriber, index) => [
  { subscriberId: subscriber.id, userId: `${subscriber.id}-owner`, name: subscriber.ownerEmail.split("@")[0] ?? "Owner", email: subscriber.ownerEmail, role: "Owner", status: "Active", lastActiveAt: subscriber.lastActivityAt, invitedAt: subscriber.createdAt },
  { subscriberId: subscriber.id, userId: `${subscriber.id}-admin`, name: `Admin ${index + 1}`, email: `admin${index + 1}@example.com`, role: subscriber.type === "Household" ? "Admin" : "Team member", status: index % 2 ? "Invited" : "Active", lastActiveAt: index % 2 ? null : "2026-09-22T10:00:00-04:00", invitedAt: "2026-09-10" }
]);

export const camerasSeed: Camera[] = subscribersSeed.flatMap((subscriber, subIndex) => Array.from({ length: Math.min(subscriber.cameraCount, 5) }, (_, index) => ({
  subscriberId: subscriber.id,
  cameraId: `CAM-${subscriber.id.replace(/\D/g, "")}-${index + 1}`,
  name: index === 0 ? "Family Room" : subscriber.type === "Organization" ? `Location ${index + 1}` : `Camera ${index + 1}`,
  status: subscriber.status === "Canceled" || (subIndex + index) % 5 === 0 ? "Offline" : "Online",
  health: (subIndex + index) % 4 === 0 ? "Degraded" : subscriber.status === "Canceled" ? "Offline" : "Healthy",
  power: index % 3 === 0 ? "Wired" : `${82 - index * 7}% battery`,
  thermal: index === 2 ? "Elevated" : "Nominal",
  storagePct: 31 + index * 9,
  uploadQueue: (subIndex + index) % 4 === 0 ? 18 + index : 0,
  lastHeartbeatAt: index % 4 === 0 ? "2026-09-22T20:31:00-04:00" : "2026-09-23T00:43:00-04:00",
  nightAssist: index % 3 === 0 ? "On" : index % 3 === 1 ? "Off" : "Unavailable",
  reviewFlagged: false
})));

export const eventsSeed: EventMeta[] = camerasSeed.flatMap((camera, index) => Array.from({ length: 3 }, (_, offset) => ({
  subscriberId: camera.subscriberId,
  eventId: `EVT-${camera.cameraId}-${offset + 1}`,
  cameraId: camera.cameraId,
  cameraName: camera.name,
  detectedAt: new Date(Date.parse("2026-09-22T20:40:00-04:00") - (index * 13 + offset * 17) * 60000).toISOString(),
  category: offset === 1 ? "Animal" : "Person",
  recording: offset === 2 && index % 4 === 0 ? "Not recorded" : "Recorded",
  durationSec: offset === 2 ? null : 17 + offset * 9,
  uploadStatus: offset === 0 ? "Uploaded" : offset === 1 ? "Queued" : camera.subscriberId.includes("2175") ? "Held - billing" : "Failed"
})));

export const codesSeed: DiscountCode[] = [
  { code: "PILOT25", description: "Commercial pilot discount", discountType: "percent", discountValue: 25, eligibility: "Commercial", expiresAt: "2026-12-31", maxRedemptions: 50, redeemedCount: 8, status: "Active", createdBy: "eja@banxito.com" },
  { code: "FRIENDS20", description: "Friends and family", discountType: "percent", discountValue: 20, eligibility: "Consumer", expiresAt: "2026-10-31", maxRedemptions: 100, redeemedCount: 31, status: "Active", createdBy: "eja@banxito.com" },
  { code: "TRIAL30", description: "Trial extension goodwill", discountType: "fixed", discountValue: 3000, eligibility: "Both", expiresAt: null, maxRedemptions: null, redeemedCount: 12, status: "Paused", createdBy: "support@reevir.app" }
];

export const adminTeamSeed: AdminTeamMember[] = [
  { id: "ADM-100", name: "Eddy Augustin", email: "eja@banxito.com", role: "SUPER_ADMIN", status: "Active", mfaStatus: "Enabled", lastSignInAt: "2026-09-23T09:58:00-04:00", invitedBy: "System", createdAt: "2026-09-01" },
  { id: "ADM-101", name: "Dana Whitfield", email: "dana.w@reevir.com", role: "BILLING_ADMIN", status: "Active", mfaStatus: "Enabled", lastSignInAt: "2026-09-22T17:31:00-04:00", invitedBy: "Eddy Augustin", createdAt: "2026-09-10" },
  { id: "ADM-102", name: "Support Desk", email: "support@reevir.app", role: "SUPPORT_ADMIN", status: "Active", mfaStatus: "Required", lastSignInAt: "2026-09-22T14:22:00-04:00", invitedBy: "Eddy Augustin", createdAt: "2026-09-11" },
  { id: "ADM-103", name: "Operations Queue", email: "ops@reevir.app", role: "OPERATIONS_ADMIN", status: "Invited", mfaStatus: "Required", lastSignInAt: null, invitedBy: "Dana Whitfield", createdAt: "2026-09-21" }
];

export const permissionRows: { label: string; permission: Permission; description: string }[] = [
  { label: "View subscribers and health", permission: "member.resendInvite", description: "Read subscriber, camera, and event metadata" },
  { label: "Extend trials", permission: "subscription.extendTrial", description: "Support goodwill and beta extension" },
  { label: "Change plans", permission: "subscription.changePlan", description: "Move subscribers between consumer and business plans" },
  { label: "Cancel/reactivate subscriptions", permission: "subscription.cancel", description: "Restrict or restore paid access" },
  { label: "Manage rebate codes", permission: "code.manage", description: "Create, pause, expire, and apply codes" },
  { label: "Flag cameras for review", permission: "camera.markReview", description: "Operational review for degraded cameras and upload backlogs" },
  { label: "Invite admin team", permission: "admin.invite", description: "Invite internal Admin Portal users" },
  { label: "Change admin roles", permission: "admin.manageRole", description: "Promote, demote, or change internal access" },
  { label: "Disable admin access", permission: "admin.disable", description: "Remove internal Admin Portal access" },
  { label: "Reset admin MFA", permission: "admin.resetMfa", description: "Help an internal user recover MFA" }
];

export const auditSeed: AuditRow[] = [
  { id: "AUD-100", occurredAt: "2026-09-23T00:22:00-04:00", admin: "Eddy Augustin", role: "SUPER_ADMIN", action: "Extend trial", targetType: "subscription", target: "Reevir Household", targetId: "HH-1001", subscriberId: "HH-1001", subscriberType: "Household", reason: "Beta validation", result: "Success", correlationId: "a8fc5c35-2f1a-44f4-9f2d-286241740100", before: { trialEndsAt: "2026-10-01" }, after: { trialEndsAt: "2026-10-31" } },
  { id: "AUD-101", occurredAt: "2026-09-22T18:03:00-04:00", admin: "Billing Ops", role: "BILLING_ADMIN", action: "Apply code", targetType: "subscription", target: "Banxito Operations", targetId: "ORG-2040", subscriberId: "ORG-2040", subscriberType: "Organization", reason: "Pilot pricing", result: "Success", correlationId: "07045272-45f1-4118-83cb-4d1c93560101", before: { code: "" }, after: { code: "PILOT25" } },
  { id: "AUD-102", occurredAt: "2026-09-22T16:48:00-04:00", admin: "Support Desk", role: "SUPPORT_ADMIN", action: "Mark camera for review", targetType: "camera", target: "Location 1", targetId: "CAM-2175-1", subscriberId: "ORG-2175", subscriberType: "Organization", reason: "Upload backlog", result: "Success", correlationId: "7ae58fdc-2363-4761-9589-765ef5c90102", before: { reviewFlagged: "false" }, after: { reviewFlagged: "true" } }
];

export const metroSeed = [
  { metro: "Miami", subscribers: 36, cameras: 71 },
  { metro: "New York", subscribers: 28, cameras: 95 },
  { metro: "Charlotte", subscribers: 18, cameras: 55 },
  { metro: "Orlando", subscribers: 14, cameras: 22 },
  { metro: "Atlanta", subscribers: 12, cameras: 36 },
  { metro: "Boston", subscribers: 11, cameras: 31 },
  { metro: "Other", subscribers: 47, cameras: 86 }
];

export const plans = ["Personal", "Household", "Household Plus", "Business Starter", "Business Growth", "Business Scale"];
